import React, { useState, useEffect } from "react";
import {
  Typography,
  Radio,
  Checkbox,
  Input,
  Button,
  Card,
  Row,
  Col,
  Space,
  Switch,
  message as antMessage,
  Divider,
} from "antd";
import { SendOutlined, AimOutlined } from "@ant-design/icons";
import "./ChatbotPage.css";
import { getChainlitIframeUrl } from "../services/chainlitUrls";

const { TextArea } = Input;
const { Title } = Typography;

interface ChatbotPageProps {
  onSendMessage?: (message: string) => void;
  onBack?: () => void;
  onStartChat?: (query: string) => void;
}

const ChatbotPage: React.FC<ChatbotPageProps> = ({
  onSendMessage,
  onBack,
  onStartChat,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [elementPickerMode, setElementPickerMode] = useState<boolean>(false);

  const categories = ["古瓷器", "古書畫", "古玉器", "資產管理諮詢"];

  const services = [
    "點評",
    "確權",
    "確值",
    "諮詢(預約)",
    "市場行情與朔源(預約)",
    "推薦交易(線上線下)",
  ];

  const handleSend = () => {
    if (inputValue.trim()) {
      onStartChat?.(inputValue);
      setInputValue("");
    }
  };

  const handleClear = () => {
    setInputValue("");
  };

  // 元素选择器功能
  const toggleElementPicker = () => {
    setElementPickerMode(!elementPickerMode);
    if (!elementPickerMode) {
      antMessage.info("元素选择模式已开启,点击页面任意元素来选择");
    }
  };

  useEffect(() => {
    if (!elementPickerMode) return;

    const handleClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const target = e.target as HTMLElement;

      // 忽略UI控件元素
      if (target.closest(".element-picker-ui") || target.closest(".ant-card")) {
        return;
      }

      const elementInfo = {
        tagName: target.tagName,
        className: target.className,
        id: target.id,
        textContent: target.textContent?.slice(0, 100),
      };

      const elementDescription = `选中元素: ${elementInfo.tagName}${
        elementInfo.id ? "#" + elementInfo.id : ""
      }${
        elementInfo.className ? "." + elementInfo.className.split(" ")[0] : ""
      }\n内容: ${elementInfo.textContent}`;

      // 通过iframe发送消息
      const iframe = document.querySelector(
        'iframe[title="智元AI研究室2025"]'
      ) as HTMLIFrameElement;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: "ELEMENT_SELECTED",
            data: elementDescription,
          },
          "*"
        );
      }

      setElementPickerMode(false);
      antMessage.success("元素已发送到聊天");
    };

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest(".element-picker-ui") || target.closest(".ant-card"))
        return;

      target.style.outline = "2px solid #1890ff";
      target.style.cursor = "crosshair";
    };

    const handleMouseOut = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      target.style.outline = "";
      target.style.cursor = "";
    };

    document.addEventListener("click", handleClick, true);
    document.addEventListener("mouseover", handleMouseOver, true);
    document.addEventListener("mouseout", handleMouseOut, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("mouseover", handleMouseOver, true);
      document.removeEventListener("mouseout", handleMouseOut, true);
    };
  }, [elementPickerMode]);

  return (
    <div
      className="chatbot-page"
      style={{
        position: "relative",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      }}
    >
      {/* 元素选择器提示条 */}
      {elementPickerMode && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            padding: "16px",
            textAlign: "center",
            fontWeight: 600,
            fontSize: "15px",
            boxShadow: "0 4px 20px rgba(102, 126, 234, 0.4)",
            backdropFilter: "blur(10px)",
          }}
        >
          <AimOutlined style={{ marginRight: "12px", fontSize: "18px" }} />
          元素選擇模式已開啟 - 點擊任意元素來選擇
          <Button
            size="small"
            onClick={toggleElementPicker}
            style={{
              marginLeft: "20px",
              background: "rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "white",
            }}
          >
            取消
          </Button>
        </div>
      )}

      <div
        className="chatbot-header"
        style={{ background: "transparent", padding: "32px 24px 24px" }}
      >
        <div className="chatbot-title-section">
          <Title level={1} style={{ color: "#fff", margin: 0 }}>
            智元AI研究室2025
          </Title>
        </div>
      </div>

      <div
        style={{ maxWidth: "1600px", margin: "0 auto", padding: "0 24px 24px" }}
      >
        <Row gutter={24}>
          {/* 左侧选项面板 */}
          <Col xs={24} lg={5}>
            <Card
              title={<span style={{ color: "#fff" }}>配置選項</span>}
              style={{
                borderRadius: "16px",
                border: "1px solid rgba(255,255,255,0.1)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                background: "rgba(30, 30, 46, 0.9)",
                backdropFilter: "blur(15px)",
                position: "sticky",
                top: "24px",
              }}
              headStyle={{
                background: "rgba(102, 126, 234, 0.2)",
                borderBottom: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <div style={{ marginBottom: "28px" }}>
                <Title
                  level={4}
                  style={{
                    fontSize: "16px",
                    color: "rgba(255,255,255,0.7)",
                    marginBottom: "12px",
                  }}
                >
                  選擇類別
                </Title>
                <Radio.Group
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={{ width: "100%" }}
                >
                  <Space
                    direction="vertical"
                    style={{ width: "100%", gap: "12px" }}
                  >
                    {categories.map((category) => (
                      <Radio
                        value={category}
                        key={category}
                        style={{
                          width: "100%",
                          color: "rgba(255,255,255,0.9)",
                        }}
                      >
                        {category}
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <Title
                  level={4}
                  style={{
                    fontSize: "16px",
                    color: "rgba(255,255,255,0.7)",
                    marginBottom: "12px",
                  }}
                >
                  選擇服務
                </Title>
                <Checkbox.Group
                  value={selectedServices}
                  onChange={setSelectedServices}
                  style={{ width: "100%" }}
                >
                  <Space
                    direction="vertical"
                    style={{ width: "100%", gap: "10px" }}
                  >
                    {services.map((service) => (
                      <Checkbox
                        value={service}
                        key={service}
                        style={{
                          width: "100%",
                          color: "rgba(255,255,255,0.9)",
                        }}
                      >
                        {service}
                      </Checkbox>
                    ))}
                  </Space>
                </Checkbox.Group>
              </div>

              <Divider style={{ borderColor: "rgba(255,255,255,0.1)" }} />

              <div className="element-picker-ui">
                <Title
                  level={4}
                  style={{
                    fontSize: "16px",
                    color: "rgba(255,255,255,0.7)",
                    marginBottom: "12px",
                  }}
                >
                  🎯 元素選擇器
                </Title>
                <Space
                  direction="vertical"
                  style={{ width: "100%", gap: "16px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Switch
                      checked={elementPickerMode}
                      onChange={toggleElementPicker}
                      checkedChildren="開啟"
                      unCheckedChildren="關閉"
                    />
                    <Button
                      type={elementPickerMode ? "primary" : "default"}
                      icon={<AimOutlined />}
                      onClick={toggleElementPicker}
                      size="small"
                    >
                      {elementPickerMode ? "選擇中..." : "選擇元素"}
                    </Button>
                  </div>
                  <div
                    style={{ fontSize: "12px", color: "rgba(255,255,255,0.5)" }}
                  >
                    開啟後點擊頁面任意元素,將其信息發送到聊天
                  </div>
                </Space>
              </div>
            </Card>
          </Col>

          {/* 右侧聊天区域 */}
          <Col xs={24} lg={19}>
            <Card
              title={
                <span style={{ color: "#fff" }}>💬 Chainlit 聊天機器人</span>
              }
              style={{
                height: "700px",
                borderRadius: "16px",
                border: "1px solid rgba(255,255,255,0.1)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                background: "rgba(30, 30, 46, 0.9)",
                backdropFilter: "blur(15px)",
              }}
              headStyle={{
                background: "rgba(102, 126, 234, 0.2)",
                borderBottom: "1px solid rgba(255,255,255,0.1)",
              }}
              bodyStyle={{
                height: "650px",
                padding: "0",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <iframe
                // src="http://localhost:8000/chainlit/"
                src={getChainlitIframeUrl()}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  borderRadius: "6px",
                }}
                title="智元AI研究室2025"
              />
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ChatbotPage;
